import { useApiQueryWithAtom } from "@/api/hooks/useApiQuery/useApiQueryWithAtom";
import { getMetrics } from "@/entities/Finance/Finance.queries";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import {
    financeMonthlyQueryAtom,
    financeQuarterlyQueryAtom,
    financeYearlyQueryAtom,
    finLabMonthlyQueryAtom,
    finLabQuarterlyQueryAtom,
    finLabYearlyQueryAtom,
    financeMonthlyQueryAtomBS,
    financeQuarterlyQueryAtomBS,
    financeYearlyQueryAtomBS,
    finLabMonthlyQueryAtomBS,
    finLabQuarterlyQueryAtomBS,
    finLabYearlyQueryAtomBS,
    finLabProjectionsQueryAtom,
    incomeStatementResolutionAtom,
    balanceSheetResolutionAtom,
    projectionsResolutionAtom,
} from "../metrics.state";
import { useAtomValue } from "jotai";
import { getFinanceLabMetrics } from "@/entities/FinanceLab/FinanceLab.queries";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";
import {
    PROJECTED_FINANCIALS_METRICS_LABELS,
    POST_CLOSING_METRICS_LABELS,
} from "../metrics.const";

export function useMetricHydration() {
    const incomeStatementResolution = useAtomValue(
        incomeStatementResolutionAtom,
    );
    const balanceSheetResolution = useAtomValue(balanceSheetResolutionAtom);

    // Determine which query atoms to use for Income Statement based on periodKind
    const isFinLabQueryAtom =
        incomeStatementResolution.periodKind === "quarterly"
            ? finLabQuarterlyQueryAtom
            : incomeStatementResolution.periodKind === "yearly"
              ? finLabYearlyQueryAtom
              : finLabMonthlyQueryAtom;

    const isFinanceQueryAtom =
        incomeStatementResolution.periodKind === "quarterly"
            ? financeQuarterlyQueryAtom
            : incomeStatementResolution.periodKind === "yearly"
              ? financeYearlyQueryAtom
              : financeMonthlyQueryAtom;

    // Determine which query atoms to use for Balance Sheet based on its own periodKind
    // Use dedicated BS atoms to keep data separate
    const bsFinLabQueryAtom =
        balanceSheetResolution.periodKind === "quarterly"
            ? finLabQuarterlyQueryAtomBS
            : balanceSheetResolution.periodKind === "yearly"
              ? finLabYearlyQueryAtomBS
              : finLabMonthlyQueryAtomBS;

    const bsFinanceQueryAtom =
        balanceSheetResolution.periodKind === "quarterly"
            ? financeQuarterlyQueryAtomBS
            : balanceSheetResolution.periodKind === "yearly"
              ? financeYearlyQueryAtomBS
              : financeMonthlyQueryAtomBS;

    // Income Statement - FinanceLab metrics
    useApiQueryWithAtom(getFinanceLabMetrics, isFinLabQueryAtom, {
        key: [
            "finance-lab-metrics-is",
            incomeStatementResolution.periodKind,
            incomeStatementResolution.window || "all",
        ],
        params: {
            labels: [
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
            ],
            periodKind: incomeStatementResolution.periodKind,
        },
    });

    // Income Statement - Finance API metrics
    useApiQueryWithAtom(getMetrics, isFinanceQueryAtom, {
        key: [
            "finance-api-metrics-is",
            incomeStatementResolution.periodKind,
            incomeStatementResolution.window || "all",
        ],
        params: {
            labels: [
                AccountLabel.REVENUES,
                AccountLabel.GROSS_MARGIN,
                AccountLabel.EBITDA,
                AccountLabel.EBITDA_MARGIN,
                AccountLabel.NET_INCOME,
            ],
            periodKind: incomeStatementResolution.periodKind,
        },
    });

    // Balance Sheet - FinanceLab metrics (using BS resolution and separate atoms)
    useApiQueryWithAtom(getFinanceLabMetrics, bsFinLabQueryAtom, {
        key: [
            "finance-lab-metrics-bs",
            balanceSheetResolution.periodKind,
            balanceSheetResolution.window || "all",
        ],
        params: {
            labels: [
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
            ],
            periodKind: balanceSheetResolution.periodKind,
        },
    });

    // Balance Sheet - Finance API metrics (using BS resolution and separate atoms)
    useApiQueryWithAtom(getMetrics, bsFinanceQueryAtom, {
        key: [
            "finance-api-metrics-bs",
            balanceSheetResolution.periodKind,
            balanceSheetResolution.window || "all",
        ],
        params: {
            labels: [
                AccountLabel.ASSETS,
                AccountLabel.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
                AccountLabel.CURRENT_ASSETS_NET_RECEIVABLES,
                AccountLabel.CURRENT_ASSETS_NET_INVENTORY,
                AccountLabel.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT,
                AccountLabel.LIABILITIES,
                AccountLabel.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
                AccountLabel.CURRENT_LIABILITIES_DEBT,
            ],
            periodKind: balanceSheetResolution.periodKind,
        },
    });

    // Projections - FinanceLab metrics (using projections resolution atom)
    // Fetch both Projected Financials and Post-Closing Metrics together
    const projectionsResolution = useAtomValue(projectionsResolutionAtom);
    useApiQueryWithAtom(getFinanceLabMetrics, finLabProjectionsQueryAtom, {
        key: [
            "finance-lab-projections",
            projectionsResolution.periodKind,
            projectionsResolution.window || "all",
        ],
        params: {
            labels: Array.from(
                new Set([
                    ...PROJECTED_FINANCIALS_METRICS_LABELS,
                    ...POST_CLOSING_METRICS_LABELS,
                ]),
            ),
            periodKind: projectionsResolution.periodKind,
        },
    });
}
